/**
 * Pick documents for a review from the person's own iManage account: choose
 * a library, search it, pick a Word or PDF document. Offered only when iManage
 * is connected; it uses the iManage routes as they stand (`state/imanage`),
 * with the person's own access, and hands back plain files to be read like
 * any upload.
 *
 * @module components/apps/contractIntelligence/IManagePickDialog
 */
import { FileTextIcon, Loader2Icon } from "lucide-react";
import { useEffect, useState } from "react";

import { base64ToBytes, imanageRequest } from "../../../state/imanage";
import { Button } from "../../ui/button";
import { Dialog, DialogClose, DialogDescription, DialogFooter, DialogHeader, DialogPanel, DialogPopup, DialogTitle } from "../../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../../ui/select";
import { CiSearch } from "./ciParts";
import { DOCX_MEDIA_TYPE } from "./extractDocumentText";

interface Entry {
  readonly id: string;
  readonly name: string;
  readonly kind: string;
  readonly extension?: string;
  readonly modified?: string;
}

const entriesOf = (value: unknown): ReadonlyArray<Entry> =>
  typeof value === "object" && value !== null && Array.isArray((value as { entries?: unknown }).entries)
    ? ((value as { entries: ReadonlyArray<Entry> }).entries ?? [])
    : [];

const reviewable = (entry: Entry) => entry.kind === "document" && ["docx", "pdf"].includes((entry.extension ?? "").toLowerCase());

export function IManagePickDialog({
  organizationId,
  onClose,
  onPick,
}: {
  readonly organizationId: string;
  readonly onClose: () => void;
  readonly onPick: (files: ReadonlyArray<File>) => void;
}) {
  const [libraries, setLibraries] = useState<ReadonlyArray<Entry> | null>(null);
  const [libraryId, setLibraryId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<ReadonlyArray<Entry> | null>(null);
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    imanageRequest(organizationId, "libraries", {}, undefined, controller.signal).then(
      (value) => {
        const list = entriesOf(value);
        setLibraries(list);
        setLibraryId((current) => current ?? list[0]?.id ?? null);
      },
      (cause: unknown) => {
        if (!controller.signal.aborted) setError(cause instanceof Error ? cause.message : "iManage could not be reached.");
      },
    );
    return () => controller.abort();
  }, [organizationId]);

  useEffect(() => {
    if (!libraryId || query.trim().length < 2) {
      setResults(null);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      // A new search answers for itself: an earlier failure's alert does not linger beside it.
      setError(null);
      imanageRequest(organizationId, "search", { libraryId, query: query.trim() }, undefined, controller.signal).then(
        (value) => setResults(entriesOf(value).filter(reviewable)),
        (cause: unknown) => {
          if (controller.signal.aborted) return;
          // Never show the previous query's results under this query's error.
          setResults([]);
          setError(cause instanceof Error ? cause.message : "The search failed.");
        },
      );
    }, 300);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [libraryId, organizationId, query]);

  const pick = async (entry: Entry) => {
    if (!libraryId) return;
    setLoading(entry.id);
    setError(null);
    try {
      const document = (await imanageRequest(organizationId, "document", { libraryId, documentId: entry.id })) as { content?: unknown; name?: unknown };
      if (typeof document.content !== "string") throw new Error("iManage returned no content.");
      const extension = (entry.extension ?? "docx").toLowerCase();
      const name = /\.[a-z0-9]{2,5}$/i.test(entry.name) ? entry.name : `${entry.name}.${extension}`;
      const bytes = base64ToBytes(document.content);
      onPick([new File([bytes as BlobPart], name, { type: extension === "pdf" ? "application/pdf" : DOCX_MEDIA_TYPE })]);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The document could not be read from iManage.");
      setLoading(null);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <DialogPopup className="flex max-h-[80vh] max-w-lg flex-col">
        <DialogHeader>
          <DialogTitle>Add from iManage</DialogTitle>
          <DialogDescription>Search your libraries with your own iManage access. Word and PDF documents can be reviewed.</DialogDescription>
        </DialogHeader>
        <DialogPanel className="min-h-0 flex-1 space-y-3 overflow-y-auto">
          {libraries === null && !error ? (
            <p className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2Icon aria-hidden className="size-3.5 animate-spin" />
              Opening your libraries…
            </p>
          ) : libraries && libraries.length > 0 ? (
            <div className="flex flex-col gap-2 sm:flex-row">
              <Select value={libraryId ?? ""} onValueChange={(next) => setLibraryId(next || null)}>
                <SelectTrigger size="sm" className="sm:w-44" aria-label="Library">
                  <span className="truncate">{libraries.find((library) => library.id === libraryId)?.name ?? "Library"}</span>
                </SelectTrigger>
                <SelectContent>
                  {libraries.map((library) => (
                    <SelectItem key={library.id} value={library.id}>
                      {library.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <CiSearch value={query} onChange={setQuery} label="Search iManage" placeholder="Search by name or number" className="sm:flex-1" />
            </div>
          ) : null}
          {results === null ? (
            <p className="px-1 text-xs text-muted-foreground">Type at least two characters to search.</p>
          ) : results.length === 0 ? (
            <p className="px-1 text-xs text-muted-foreground">No Word or PDF documents match.</p>
          ) : (
            <ul className="divide-y divide-border/50 overflow-hidden rounded-xl border border-border/70">
              {results.map((entry) => (
                <li key={entry.id}>
                  <button
                    type="button"
                    disabled={loading !== null}
                    onClick={() => void pick(entry)}
                    className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[13px] outline-none hover:bg-accent/40 focus-visible:bg-accent/50 disabled:opacity-60"
                  >
                    {loading === entry.id ? (
                      <Loader2Icon aria-hidden className="size-4 animate-spin text-muted-foreground" />
                    ) : (
                      <FileTextIcon aria-hidden className="size-4 text-muted-foreground" />
                    )}
                    <span className="min-w-0 flex-1 truncate">{entry.name}</span>
                    <span className="text-xs uppercase text-muted-foreground">{entry.extension}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {error ? (
            <p className="text-xs text-destructive" role="alert">
              {error}
            </p>
          ) : null}
        </DialogPanel>
        <DialogFooter>
          <DialogClose render={<Button size="sm" variant="ghost">Close</Button>} />
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
